import { t } from 'i18next';
import type { IObservableArray } from 'mobx';
import moment from 'moment-timezone';

import type { Goal, Recommendation, TTDCampaign } from '@feathr/blackbox';
import { ERecommendationStatus, ERecommendationType } from '@feathr/blackbox';
import { toast } from '@feathr/components';

type TRecommendationContext = 'report' | 'recommendation';

interface IOnChangeRecommendationStatusProps {
  /** The recommendation model */
  recommendation: Recommendation;
  /** Used to determine if the recommendation was applied (recommendation) or auto_dismissed (campaign) */
  context?: TRecommendationContext;
  /** Refreshes the recommendations collection to see if any recommendation statuses have changed */
  onRefreshCollection: () => Promise<void>;
}

interface IRecommendationPostSaveProps extends IOnChangeRecommendationStatusProps {
  /** The type of recommendation */
  type: ERecommendationType;
  /** The Tradedesk campaign */
  campaign: TTDCampaign;
  /** The user's timezone from account settings */
  timezone: string;
  /** The goals for the campaign */
  goals: IObservableArray<Goal>;
}

/**
 * Returns true if the new target_value (budget) is greater than the current target_value
 * @param campaign - The Tradedesk campaign model
 * @param recommendation - The recommendation model
 * @returns boolean
 */
function getBudgetStatus(campaign: TTDCampaign, recommendation: Recommendation): boolean {
  const newTargetValue = campaign.get('exposure_settings')?.target_value ?? 0;
  const currentTargetValue =
    recommendation.get('current_campaign_values')?.exposure_settings?.target_value ?? 0;
  return newTargetValue > currentTargetValue;
}

/**
 * Returns true if the new date_end is the same or after the recommended date_end
 * @param campaign - The Tradedesk campaign model
 * @param recommendation - The recommendation model
 * @param timezone - The user's timezone from user settings
 * @returns boolean
 */
function getDateStatus(
  campaign: TTDCampaign,
  recommendation: Recommendation,
  timezone: string,
): boolean {
  const newDateEnd = campaign.get('date_end');
  const newMoment = moment.utc(newDateEnd).tz(timezone);

  const recommendedDateEnd = recommendation.get('proposed_campaign_values').date_end;
  const recommendedMoment = moment.utc(recommendedDateEnd).tz(timezone);

  return newMoment.isSameOrAfter(recommendedMoment);
}

/**
 * Updates the status of a recommendation (applied or auto_dismissed) based on the context of the recommendation
 * @param recommendation - The recommendation model
 * @param context - The context of the recommendation (report or recommendation)
 * @param onRefreshCollection - Function to refresh the recommendations collection
 * @returns a function that updates the status of a recommendation
 */
async function handleChangeRecommendationStatus({
  recommendation,
  context = 'recommendation',
  onRefreshCollection,
}: IOnChangeRecommendationStatusProps): Promise<void> {
  const isRecommendationContext = context === 'recommendation';
  const status = ERecommendationStatus.Applied;

  recommendation.set({ status });

  try {
    await recommendation.patchDirty();

    if (isRecommendationContext) {
      toast(t('Applied recommendation: {{title}}', { title: recommendation.get('title') }), {
        type: 'info',
      });
    } else {
      toast(
        t('Dismissed recommendation after changes were made in the wizard: {{title}}', {
          title: recommendation.get('title'),
        }),
        {
          type: 'info',
        },
      );
    }

    await onRefreshCollection();
  } catch (e) {
    toast(
      t('An error occurred while updating the status of a recommendation: {{title}}', {
        title: recommendation.get('title'),
      }),
    );
  }
}

/**
 * Updates the status of a recommendation based on the type of recommendation.
 * This will be done by the backend in the future.
 * @param type - The type of recommendation
 * @param campaign - The Tradedesk campaign model
 * @param recommendation - The recommendation model
 * @param onRefreshCollection - Function to refresh the recommendations collection
 * @param timezone - The user's timezone from user settings
 * @param context - The context of the recommendation (campaign or recommendation)
 * @param goals - The goals for the campaign
 * @returns a function that updates the status of a recommendation
 */
export function updateRecommendationStatus({
  type,
  campaign,
  recommendation,
  onRefreshCollection,
  timezone,
  context,
  goals,
}: IRecommendationPostSaveProps): () => Promise<void> {
  const handleApplyRecommendation = async (): Promise<void> => {
    await handleChangeRecommendationStatus({
      recommendation,
      onRefreshCollection,
      context,
    });
  };

  const recommendationMap = {
    [ERecommendationType.Budget]: async (): Promise<void> => {
      const shouldChangeStatus = getBudgetStatus(campaign, recommendation);

      // Don't apply the recommendation unless the new target value is greater than the current target value.
      // This allows the user to partially apply the recommendation without going under the original.
      if (shouldChangeStatus) {
        handleApplyRecommendation();
      }
    },
    [ERecommendationType.ExtendCampaign]: async (): Promise<void> => {
      const shouldChangeStatus = getDateStatus(campaign, recommendation, timezone);

      // Don't apply the recommendation unless the new date_end is the same or after the recommended date_end.
      if (shouldChangeStatus) {
        handleApplyRecommendation();
      }
    },
    [ERecommendationType.BudgetDuration]: async (): Promise<void> => {
      const hasLargerBudget = getBudgetStatus(campaign, recommendation);
      const hasRecommendedDateEndOrLater = getDateStatus(campaign, recommendation, timezone);

      // Don't apply the recommendation unless the new target value is greater than the current target value
      // and the new date_end is the same or after the recommended date_end.
      // This allows the user to partially apply the recommendation without going under the original.
      if (hasLargerBudget && hasRecommendedDateEndOrLater) {
        handleApplyRecommendation();
      }
    },
    [ERecommendationType.MissingGoal]: async (): Promise<void> => {
      const hasSavedGoal = goals?.some((goal) => !goal.isEphemeral);

      // If the user saved a goal, apply the recommendation
      if (hasSavedGoal) {
        handleApplyRecommendation();
      }
    },
    [ERecommendationType.EnableOmnitargeting]: async (): Promise<void> => {
      // TODO: Implement
      return;
    },
    [ERecommendationType.IncreaseBaseBid]: async (): Promise<void> => {
      // TODO: Implement
      return;
    },
    [ERecommendationType.IncreaseFrequencyCap]: async (): Promise<void> => {
      // TODO: Implement
      return;
    },
    [ERecommendationType.UpdateCreatives]: async (): Promise<void> => {
      // TODO: Implement
      return;
    },
  };

  return recommendationMap[type];
}
